import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CookieOverlay from 'components/CookieOverlay';
import LoadingDots from 'components/LoadingDots';
import ScrollToTop from 'components/ScrollToTop';
import { useAppState } from 'hooks/useAppState';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { TrackRouteChanges } from 'hooks/useTrackRouteChanges';
import AbTestsRoute from 'modules/ab-tests';
import AppShell from 'modules/app/AppShell';
import AppUrlListener from 'modules/app/AppUrlListener';
import ModalOverlays from 'modules/app/ModalOverlays';
import RouteChangeTracker from 'modules/app/RouteChangeTracker';
import { createNavigationalRoute } from 'modules/app/components/NavigationalLayout';
import PasswordRoutes from 'modules/authentication/PasswordRoutes';
import { AuthCheck } from 'modules/authentication/helpers/auth';
import CheckoutRoutes from 'modules/checkout';
import ConfirmationRoute from 'modules/confirmation';
import CourseRoutes from 'modules/course';
import { NotFoundComponent } from 'modules/errorpages/Notfound';
import ServerError from 'modules/errorpages/ServerError';
import ExperienceRoutes from 'modules/experience';
import ExperiencesRoutes from 'modules/experiences';
import FavoritesRoutes from 'modules/favorites';
import FeatureFlagRoute from 'modules/feature-flags';
import DefaultHead from 'modules/head';
import HomescreenRoutes from 'modules/homescreen';
import HostRoute from 'modules/host';
import HostAgendaRoutes from 'modules/host-agenda';
import HostExperiencesRoutes from 'modules/host-experiences';
import {
  HostHomePageRoutes,
  HostHomePageWidgetRoutes,
} from 'modules/host-homescreen';
import HostLearningCenter from 'modules/host-learning-center';
import HostOnboardingPageRoute from 'modules/host-onboarding';
import HostPayoutRoutes from 'modules/host-payout-methods';
import PersonalizedOfferRoutes from 'modules/host-personalized-offer';
import HostProfile from 'modules/host-profile';
import HostStatusRoutes from 'modules/host-status';
import HostTransferBooking from 'modules/host-transfer-booking';
import InfoPagesRoutes from 'modules/info-pages';
import InvitationRoutes from 'modules/invite';
import AppPromoRoute from 'modules/landings/app-promo';
import SustainabilityRoute from 'modules/landings/sustainability';
import LocalSearchRoutes from 'modules/locals';
import ManageBookingRoutes from 'modules/manage-booking';
import OverviewPageRoutes from 'modules/overview';
import PrismicRoutes from 'modules/prismic';
import PrivacyRoute from 'modules/privacy';
import RateAppRoute from 'modules/rate-app';
import RescheduleRoutes from 'modules/reschedule';
import ReviewPageRoute from 'modules/review-page';
import SearchRoutes from 'modules/search';
import UserEnvironment2Routes from 'modules/user-environment2';
import WriteReviewRoutes from 'modules/write-review';
import { CookieConsentContextProvider } from 'providers/CookieConsentProvider';
import { FavoritesContextProvider } from 'providers/FavoritesProvider';
import React, { useEffect, useRef, useState } from 'react';
import type { Location as RouterLocation } from 'react-router-dom';
import { Routes, useLocation, useNavigationType } from 'react-router-dom';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { stripLocaleFromPath } from 'routing/paths';
import { scrollToFocusedOnKeyboard } from 'utils/onResize';
import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import { CurrencyContextProvider } from '~/context/CurrencyContext';
import { CurrenciesListContextProvider } from '~/context/CurrencyListContext';
import { UserEnvironmentContextProvider } from '~/context/UserEnvironmentContext';
import { ExperimentsProvider } from '~/experiments';
import { appServices } from '~/singletons';
import { useInAppUpdateStore } from '~/stores/inAppUpdate';

import { initCapacitorLate } from './capacitor';

export const queryClient = new QueryClient();

export type ModalLocationState = { modal?: boolean };

async function initCapacitorPlugins(navigate: NavigateFunction) {
  appServices.permissionFunctions.startPush();

  await initCapacitorLate(navigate, () => {
    useInAppUpdateStore
      .getState()
      .checkForUpdate({ startWhenAvailable: false, trigger: 'auto' });
  });
}

const App = () => {
  const navigationType = useNavigationType();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  type ModalRouterLocation = RouterLocation & { state?: ModalLocationState };
  const previousLocation = useRef<ModalRouterLocation>(
    location as ModalRouterLocation,
  );
  const [underlyingLocation, setUnderlyingLocation] =
    useState<ModalRouterLocation | null>(null);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const { checkForUpdate } = useInAppUpdateStore();

  useAppState();

  useEffect(() => {
    if (isCordova() && hasHydrated) {
      void checkForUpdate({ startWhenAvailable: true, trigger: 'auto' });
      setTimeout(async () => {
        await initCapacitorPlugins(navigate);
      });
    }
    const unsubscribe = scrollToFocusedOnKeyboard();
    return () => {
      unsubscribe();
    };
  }, [navigate, checkForUpdate, hasHydrated]);
  const queryModal =
    typeof window !== 'undefined'
      ? new URLSearchParams(location.search).get('modal') === '1'
      : false;
  const isModal = Boolean(location?.state?.modal || queryModal);
  useEffect(() => {
    if (isModal) {
      // If we are opening a modal, we want to keep the previous location
      // so we can render it in the background.
      setUnderlyingLocation(previousLocation.current);
    } else {
      setUnderlyingLocation(location);
    }
    previousLocation.current = location;
  }, [isModal, location]);

  // ---- Safe background routing for modals (guard against base mismatches) ----
  // Use previous location as background when modal is active; no base guard needed
  const routesLocation = underlyingLocation ?? location;

  // Normalized locations for route matching (strip leading locale)
  const mainRoutesLocation: RouterLocation = {
    ...routesLocation,
    pathname: stripLocaleFromPath(routesLocation.pathname),
  };
  const modalRoutesLocation: RouterLocation = {
    ...location,
    pathname: stripLocaleFromPath(location.pathname),
  };

  if (!hasHydrated) {
    return <LoadingDots />;
  }

  const sharedRoutes = [
    PrismicRoutes,
    HomescreenRoutes,
    HostHomePageWidgetRoutes,
    HostHomePageRoutes,
    HostOnboardingPageRoute,
    HostLearningCenter,
    HostProfile,
    HostTransferBooking,
    SearchRoutes,
    ExperiencesRoutes,
    LocalSearchRoutes,
    ExperienceRoutes,
    CheckoutRoutes,
    ConfirmationRoute,
    HostAgendaRoutes,
    RateAppRoute,
    ReviewPageRoute,
    FavoritesRoutes,
    CourseRoutes,
    PrivacyRoute,
    AppPromoRoute,
    PersonalizedOfferRoutes,
    HostExperiencesRoutes,
    HostStatusRoutes,
    UserEnvironment2Routes,
    HostRoute,
    FeatureFlagRoute,
    AbTestsRoute,
    WriteReviewRoutes,
    InfoPagesRoutes,
    PasswordRoutes,
    ManageBookingRoutes,
    OverviewPageRoutes,
    RescheduleRoutes,
  ];

  const mainOnlyRoutes = [
    HostPayoutRoutes,
    SustainabilityRoute,
    InvitationRoutes,
    ServerError,
  ];

  const mainRoutes = (
    <Routes location={mainRoutesLocation}>
      {isModal ? null : ModalOverlays}
      {sharedRoutes}
      {mainOnlyRoutes}
      {!isModal &&
        createNavigationalRoute({
          path: '*',
          component: NotFoundComponent,
          showSearchInNavbar: true,
          transparentNavbar: true,
        })}
    </Routes>
  );

  const modalRoutes = !isModal ? null : (
    <Routes location={modalRoutesLocation}>
      {ModalOverlays}
      {sharedRoutes}
    </Routes>
  );

  return (
    <CookieConsentContextProvider>
      <QueryClientProvider client={queryClient} key="query-client">
        <ExperimentsProvider>
          <UserEnvironmentContextProvider>
            <CurrenciesListContextProvider>
              <CurrencyContextProvider>
                <FavoritesContextProvider>
                  <ScrollToTop
                    isModal={isModal}
                    navigationType={navigationType}
                    newLocation={underlyingLocation}
                  />
                  <AppUrlListener />
                  <DefaultHead />
                  <AuthCheck />
                  <TrackRouteChanges />
                  <RouteChangeTracker />
                  <AppShell
                    isModalActive={isModal}
                    main={mainRoutes}
                    belowMain={<CookieOverlay />}
                    modalLayer={modalRoutes}
                  />
                </FavoritesContextProvider>
              </CurrencyContextProvider>
            </CurrenciesListContextProvider>
          </UserEnvironmentContextProvider>
        </ExperimentsProvider>
      </QueryClientProvider>
    </CookieConsentContextProvider>
  );
};

export default App;
